import { useState } from "react";
import {
  Button,
  Form,
  FormField,
  Input,
  ProgressBar,
  ProgressBarProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { getSnapshotPresignedUrl, uploadFileToS3 } from "../../../api/snapshot";
import {
  defaultSnapshotFileName,
  getS3KeyFromDateAndObjectKey,
  s3KeyRegex,
} from "../../../utils/snapshot/snapshotUtils";
import { PresignedUrlRequest, PresignedUrlRequestMethod } from "../../../api/models/snapshotModels";
import { getCurrentFileAndUpload, getFileExtension } from "../../../utils/snapshot/excelFileUploader";
import { ExcelFileExtension, excelFileExtensionToContentType } from "../../../utils/snapshot/excelFileUtils";

const UPLOAD_SUCCESS = "Upload succeeded!";
const UPLOAD_ERROR = "An error occurred while uploading, please try again";
const FILE_NAME_INVALID = "File name contains invalid characters";
const INVALID_FILE_EXTENSION = "Invalid file type. Only Excel file extensions are supported";

function UploadSnapshot() {
  const [fileName, setFileName] = useState("");
  const [fileNameErrorText, setFileNameErrorText] = useState("");

  const [uploadProgressValue, setUploadProgressValue] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<ProgressBarProps.Status | undefined>(undefined);
  const [uploadResultText, setUploadResultText] = useState("");

  function upload() {
    const date = new Date();
    const fileNameToUpload = fileName || defaultSnapshotFileName(date);
    if (!validateFileName(fileNameToUpload)) {
      return;
    }

    onUploadStart();

    getFileExtension(onGetFileExtension(fileNameToUpload, date), onInvalidFileExtensionError);
  }

  function onGetFileExtension(fileName: string, date: Date) {
    return function (fileExtension: ExcelFileExtension) {
      const objectKey = `${fileName}${fileExtension}`;
      const presignedUrlRequest: PresignedUrlRequest = {
        keyName: getS3KeyFromDateAndObjectKey(date, objectKey),
        method: PresignedUrlRequestMethod.PUT,
      };

      getSnapshotPresignedUrl(presignedUrlRequest)
        .then((response) => {
          if (response.presignedUrl) {
            uploadWithPresignedUrl(response.presignedUrl, fileExtension);
          } else if (response.errorMessage) {
            onUploadError();
            console.error(response.errorMessage);
          }
        })
        .catch((err) => onUploadError(err));
    };
  }

  function uploadWithPresignedUrl(presignedUrl: string, fileExtension: ExcelFileExtension) {
    const uploadFile = (file: Uint8Array) => {
      uploadFileToS3(presignedUrl, file, excelFileExtensionToContentType[fileExtension])
        .then((success) => {
          if (success) {
            onUploadSuccess();
          } else {
            onUploadError();
          }
        })
        .catch((err) => onUploadError(err));
    };

    getCurrentFileAndUpload(uploadFile, setUploadProgressValue, onUploadError);
  }

  function onUploadSuccess() {
    setUploadStatus("success");
    setUploadResultText(UPLOAD_SUCCESS);
  }

  function onUploadError(error?: any) {
    setUploadStatus("error");
    setUploadResultText(error?.toString() ?? UPLOAD_ERROR);
  }

  function onInvalidFileExtensionError() {
    setUploadStatus("error");
    setUploadResultText(INVALID_FILE_EXTENSION);
  }

  function onUploadStart() {
    setUploadStatus("in-progress");
    setUploadResultText("");
    setUploadProgressValue(0);
  }

  function onFileNameChange(fileName: string) {
    setFileName(fileName);
    validateFileName(fileName);
  }

  function validateFileName(fileName: string) {
    const valid = s3KeyRegex.test(fileName);
    if (valid) {
      setFileNameErrorText("");
      return true;
    } else {
      setFileNameErrorText(FILE_NAME_INVALID);
      return false;
    }
  }

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        actions={
          <Button variant={"primary"} loading={uploadStatus === "in-progress"} onClick={upload}>
            Upload
          </Button>
        }
      >
        <SpaceBetween size={"m"} direction={"vertical"}>
          <FormField
            label={"File Name"}
            constraintText={
              "Do not include file extension (e.g., .xlsm). Inputting the name of an existing file in the same day will overwrite it."
            }
            errorText={fileNameErrorText}
          >
            <Input
              value={fileName}
              onChange={({ detail }) => onFileNameChange(detail.value)}
              placeholder={"Leave blank to use a default name"}
              disableBrowserAutocorrect
            />
          </FormField>
          {uploadStatus && <ProgressBar status={uploadStatus} value={uploadProgressValue} resultText={uploadResultText} />}
        </SpaceBetween>
      </Form>
    </form>
  );
}

export default UploadSnapshot;
